import * as React from 'react';
import {ColumnProps} from 'antd/lib/table';
import {ChecklistState, ChecklistSummary, Item, RentalItem} from '../api/dtos/dtos';
import locales from '../locales';
import ButtonGroup from 'antd/lib/button/button-group';
import {Button, Modal, Table} from 'antd';
import RentalItemForm from './RentalItemForm';
import ItemSelector from '../item/ItemSelector';
import {renderStateElementCondensed, RentalState} from '../common/state';
import {AmountType, getAmountTypeLabel, getPriceForItem} from 'src/common/AmountType';
import {simpleColumn} from '../utils/table-utils';
import {alignRightNoWrap, groupNumber, renderMmColumn, renderPriceColumn} from '../utils/number-utils';
import ButtonBar from 'src/common/ButtonBar';
import {EditorMode} from '../common/types';
import {Link} from 'react-router-dom';
import * as paths from '../paths';
import {ItemType} from "../item-details/itemType";
import {renderItemName} from "../item/item-name-renderer";
import {findChecklistByRentalItemId} from "../checklist/dtos";
import ChecklistStateLabel from "../checklist/ChecklistStateLabel";


interface RentalItemDetailsEditorProps {
  mode: EditorMode;
  editable: boolean;
  rentalItems: RentalItem[];
  onRentalItemsChange: (rentalItems: RentalItem[]) => void;
  showChecklist: boolean;
  checklistSummary?: ChecklistSummary;
  onChecklistAdd?: (item: RentalItem) => void;
  onChecklistEdit?: (checklistId: number) => void;
}

interface RentalItemDetailsEditorState {
  editedRentalItem?: RentalItem;
  // After edit from is submitted we use this to manipulate the rentalItems list
  editedRentalItemIdx?: number;
  itemSelectorVisible: boolean;
}

class RentalItemsEditor extends React.Component<RentalItemDetailsEditorProps, RentalItemDetailsEditorState> {

  public readonly state: RentalItemDetailsEditorState = {
    itemSelectorVisible: false,
  };

  public render(): React.ReactNode {

    const columns: Array<ColumnProps<RentalItem>> = [
      {
        ...simpleColumn('', r => renderStateElementCondensed(r.state)),
        key: 'state',
        width: 38,
      },
      simpleColumn(locales.item, r => <Link to={paths.viewItemPath(r.item.id)}>{renderItemName(r.item)}</Link>),
      simpleColumn(locales.inventoryNumber, r => r.item.inventoryNumber),
      {
        ...simpleColumn(locales.caution, r => renderPriceColumn(r.caution)),
        className: 'align-right',
      },
      simpleColumn(locales.comment, r => r.comment),
      {
        ...simpleColumn(locales.operationHours, r => alignRightNoWrap(groupNumber(r.rentalOperationHours))),
        className: 'align-right',
      }, {
        ...simpleColumn(locales.abrasion_dimension, r => renderMmColumn(r.rentalAbrasionMm)),
        className: 'align-right',
      }, {
        ...simpleColumn(locales.net_price, r => renderPriceColumn(r.price)),
        className: 'align-right',
      },
      simpleColumn(locales.settlement, r => getAmountTypeLabel(r.amountType)),
    ];

    if (this.props.showChecklist) {
      columns.push({
          ...simpleColumn(locales.checklist, r => this.renderChecklistColumn(r), 190),
      });
    }

    if (this.props.editable) {
      columns.push({key: 'actions', width: 80, render: this.renderActionColumn});
    }

    const {rentalItems, mode, editable} = this.props;
    const {editedRentalItem, itemSelectorVisible} = this.state;
    const rowKeyFn = (record: RentalItem, index: number) => String(index);
    const excludedItemsFromSearch: Item[] = rentalItems
      .map(ri => ri.item!)
      .filter(i => !i.infiniteStock);

    return (
      <>
        <Modal
          title={locales.edit_entry}
          visible={editedRentalItem !== undefined}
          footer={null}
          onCancel={this.onRentalItemFormCancel}
          destroyOnClose={true}
        >
          <RentalItemForm
            rentalItem={editedRentalItem}
            onFormSubmit={this.onRentalItemFormSubmit}
            onFormCancel={this.onRentalItemFormCancel}
          />
        </Modal>

        <Modal
          title={locales.select_item}
          visible={itemSelectorVisible}
          footer={null}
          onCancel={this.onItemSelectorCancel}
          width="auto"
          style={{margin: '0 24px'}}
          destroyOnClose={true}
        >
          <ItemSelector
            allowedStates={[RentalState.AVAILABLE]}
            selectedStates={[RentalState.AVAILABLE]}
            excludedFromSearch={excludedItemsFromSearch}
            onSelect={this.onItemSelect}/>
        </Modal>

        {mode === 'create' && editable && (
          <ButtonBar style={{marginBottom: 8}}>
            <Button size="small" type="primary" icon="plus"
                    onClick={this.onAddRentalItemClick}>{locales.add}</Button>
          </ButtonBar>
        )}

        <Table
          style={{marginBottom: 24}}
          columns={columns}
          dataSource={rentalItems}
          rowKey={rowKeyFn}
          bordered={true}
          pagination={false}
          size="small"
          scroll={{x: 1024}}
        />
      </>
    );
  }

  private renderActionColumn = (text: any, record: RentalItem, index: number) => {
    const onEditRentalItemClick = () => this.onEditRentalItemClick(record, index);
    const onDeleteRentalItemClick = () => this.onDeleteRentalItemClick(record, index);
    return this.props.editable && (
      <div className="action-column" style={{textAlign: 'right'}}>
        <ButtonGroup>
          <Button icon="edit"
                  onClick={onEditRentalItemClick}
                  title={locales.edit}/>
          {this.isRentalItemRemovable(record) && (
            <Button icon="delete"
                    onClick={onDeleteRentalItemClick}
                    title={locales.delete}/>
          )}
        </ButtonGroup>
      </div>
    );
  };


  private renderChecklistColumn = (record: RentalItem) => {
    const onAddChecklistClick = () => this.onAddChecklistClick(record);
    const onEditChecklistClick = () => this.onEditChecklistClick(record);
    return (
      <div style={{display: "flex", alignItems: "center"}}>
        {this.checklistMandatory(record) && !this.hasChecklist(record) && (
          <Button icon="plus" onClick={onAddChecklistClick} style={{marginRight: 8, minWidth: 32, width: 32,}} />
        )}
        {this.hasChecklist(record) && (
          <Button icon="edit" onClick={onEditChecklistClick} style={{marginRight: 8, minWidth: 32, width: 32,}} />
        )}
        {this.checklistMandatory(record) &&
          this.renderChecklistState(record)
        }
      </div>
    );
  };

  private renderChecklistState = (record: RentalItem): React.ReactNode => {
    const {checklistSummary} = this.props;
    const checklist = findChecklistByRentalItemId(checklistSummary?.checklists, record.id!)
    const state = checklist?.state ?? ChecklistState.MISSING;
    return (
      <ChecklistStateLabel state={state} />
    );
  }

  private isRentalItemRemovable = (_: RentalItem): boolean => {
    // Removing rental item is only possible in create mode
    return this.props.mode === 'create';
  };

  private checklistMandatory = (record: RentalItem): boolean => {
    return record.item.checklistRequired ?? false;
  };

  private hasChecklist = (record: RentalItem): boolean => {
    return record.rentalChecklistId !== null;
  };

  private onEditRentalItemClick = (rentalItem: RentalItem, index: number) => {
    this.setState({
      editedRentalItem: rentalItem,
      editedRentalItemIdx: index,
    });
  };

  private onDeleteRentalItemClick = (rentalItem: RentalItem, index: number) => {
    const {rentalItems, onRentalItemsChange} = this.props;
    const newItems = [...rentalItems];
    newItems.splice(index, 1);
    onRentalItemsChange(newItems);
  };

  private onAddChecklistClick = (rentalItem: RentalItem) => {
    this.props.onChecklistAdd!(rentalItem);
  };

  private onEditChecklistClick = (rentalItem: RentalItem) => {
    this.props.onChecklistEdit!(rentalItem.rentalChecklistId!);
  };

  private onRentalItemFormSubmit = (item: RentalItem) => {
    const {rentalItems, onRentalItemsChange} = this.props;
    let newItems: RentalItem[];

    const idx = this.state.editedRentalItemIdx;
    if (idx === undefined) {
      // Rental item was created, add it to the end of the grid
      newItems = [...rentalItems, item];
    } else {
      // Rental item was edited, replace it in the grid with the edited one
      newItems = [...rentalItems];
      newItems.splice(idx, 1, item);
    }

    this.setState({
      editedRentalItem: undefined,
      editedRentalItemIdx: undefined,
    });
    onRentalItemsChange(newItems);
  };

  private onRentalItemFormCancel = () => {
    this.setState({
      editedRentalItem: undefined,
      editedRentalItemIdx: undefined,
    });
  };

  private onAddRentalItemClick = () => {
    this.setItemSelectorVisible(true);
  };

  private onItemSelectorCancel = () => {
    this.setItemSelectorVisible(false);
  };

  private setItemSelectorVisible = (visible: boolean) => {
    this.setState({
      itemSelectorVisible: visible,
    });
  };

  private onItemSelect = (item: Item) => {
    this.setItemSelectorVisible(false);
    this.createRentalItem(item);
  };

  private createRentalItem = (item: Item) => {
    const initialAmountType = item.itemType === ItemType.SERVICE ? AmountType.PCS : AmountType.DAILY_7_7;

    const newItem: RentalItem = {
      id: null,
      item,
      price: getPriceForItem(item, initialAmountType) || 0,
      caution: 0,
      amountType: initialAmountType,
      rentalAbrasionMm: null,
      rentalOperationHours: null,
      returnalAbrasionMm: null,
      returnalOperationHours: null,
      rentalSite: null,
      comment: null,
      rentalChecklistId: null,
      returnalChecklistId: null,
    };

    this.setState({
      editedRentalItem: newItem,
      editedRentalItemIdx: undefined,
    });
  };
}

export default RentalItemsEditor;


// WEBPACK FOOTER //
// ./src/rental-details/RentalItemsEditor.tsx